import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { Container } from '../components/_atoms';
import { ROUTES } from '../constants';
import { APIService } from '../services/apiService';
import { ApplicationHeadDTO } from '../DTO/ApplicationHeadDTO';
import { GridColDef, DataGrid, GridToolbar, GridRenderCellParams, GridValueGetter } from '@mui/x-data-grid';
import { Button, Typography, FormControlLabel, Checkbox } from '@mui/material';
import { appStatusName, getAppStatusNames } from '../util/enumerations';

const api = new APIService();

const colDefs: GridColDef[] = [
	{
		field: 'edit',
		headerName: 'Open',
		minWidth: 110,
		renderCell: (params: GridRenderCellParams<ApplicationHeadDTO>) => (
			<Button
				variant="contained"
				color="primary"
				size="small"
				component={Link}
				to={`${ROUTES.APPLICATION}/${params.row?.id}`}
			>
				Open
			</Button>
		),
	},
	{
		field: 'print',
		headerName: 'Print',
		minWidth: 110,
		renderCell: (params: GridRenderCellParams<ApplicationHeadDTO>) => (
			<Button
				variant="contained"
				style={{ marginLeft: '10px' }}
				color="secondary"
				href={api.downloadApplicationHeadById(params.row?.id)}
			>
				Print
			</Button>
		),
	},
	{ field: 'id', headerName: 'ID', width: 50, type: 'number' },
	{
		field: 'userName',
		headerName: 'Student',
		type: 'string',
		width: 200,
		valueGetter: ((value, row: ApplicationHeadDTO) => row?.user?.name) as GridValueGetter<ApplicationHeadDTO, string>,
	},
	{
		field: "year",
		headerName: "Year",
		type: "number",
		width: 70,
		// By default, the year is displayed with a separator (e.g. 2,025), which is not what we want
		valueFormatter: ((value, row: ApplicationHeadDTO) => row?.year?.toString() || ''),
	},
	{
		field: 'schoolName',
		headerName: 'University',
		width: 140,
		valueGetter: ((value, row: ApplicationHeadDTO) => row?.school?.name) as GridValueGetter<ApplicationHeadDTO, string>,
	},
	{
		field: 'status',
		headerName: 'Status',
		type: 'singleSelect',
		width: 120,
		valueOptions: getAppStatusNames(),
		valueGetter: ((value, row: ApplicationHeadDTO) => appStatusName(row?.status)) as GridValueGetter<ApplicationHeadDTO, string>,
	},
	{ field: 'total', headerName: 'Total rows', width: 150 },
	{ field: 'unhandled', headerName: 'Unhandled rows', width: 100 },
	{
		field: 'created',
		type: 'date',
		headerName: 'Created',
		width: 150,
		valueGetter: ((value, row: ApplicationHeadDTO) =>
			row?.created ? new Date(row.created) : null) as GridValueGetter<ApplicationHeadDTO, Date | null>,
	},
	{
		field: 'modified',
		headerName: 'Modified',
		type: 'date',
		width: 150,
		valueGetter: ((value, row: ApplicationHeadDTO) =>
			row?.modified ? new Date(row.modified) : null) as GridValueGetter<ApplicationHeadDTO, Date | null>,
	},
];

const ApplicationsAdmin = () => {
	const [applications, setApplications] = useState<
		ApplicationHeadDTO[] | null
	>(null);
	const [fetchArchived, setFetchArchived] = useState(false);

	const getApplications = () => {
		api.getApplications(fetchArchived).then((data) => setApplications(data));
	};

	useEffect(() => {
		getApplications();
	}, [fetchArchived]);

	if (!applications) return <p>Fetching applications...</p>;
	return (
		<Container>
			<Typography
				gutterBottom
				style={{ marginTop: '5px' }}
				variant="h4"
				align="center"
			>
				Applications
			</Typography>
			<FormControlLabel
				control={
					<Checkbox
						checked={fetchArchived}
						onChange={(e) => setFetchArchived(e.target.checked)}
					/>
				}
				label="Show archived"
				style={{ marginBottom: '10px' }}
			/>
			<div
				style={{
					height: 'calc(100vh - 250px)',
					width: '100%',
					marginTop: '10px',
				}}
			>
				<DataGrid
					columns={colDefs}
					slots={{
						toolbar: GridToolbar,
					}}
					rows={applications}
				/>
			</div>
		</Container>
	);
};

export default ApplicationsAdmin;
